import TextField from "@/components/TextField";
import Icon from "@/components/Icon";
import PeopleSelectionLists from "./components/PeopleSelectionLists";
import { AlgoliaSearchResults, type FacilityHit, getFacilityDisplayName } from "@/components/AlgoliaSearch";
import { useEffect } from "react";
import { Person, Facility } from "./CreateGlobalInteractionSideSheet";
import { InteractionInput } from "@/components/interactions/types";
import { UnavailableFacilityBanner } from "./components/UnavailableFacilityBanner";
import { renderCreateInteractionFacilityHit } from "./createInteractionFuzzyHitRenderer";
import { useCreateInteractionFuzzySearch } from "./useCreateInteractionFuzzySearch";

type FacilityType = {
  ineligibleFacility: Facility | undefined;
  loading?: boolean;
  peopleArray: Person[];
  gatheredFacilities: Facility[];
  setIneligibleFacility: (value: Facility | undefined) => void;
  setSearchedForFacility: (value: boolean) => void;
  setVariables: (value: InteractionInput) => void;
  setCurrentFacility: (value: Facility | undefined) => void;
  variables: InteractionInput;
};

const mapHitToFacility = (hit: FacilityHit, index: number): Facility => ({
  id: hit.facility_id,
  name: hit.facility_name || hit.name,
  index,
  mainAddress: {
    address1: hit.address1 || "",
    address2: hit.address2 || "",
    city: hit.city || "",
    state: hit.state || "",
    postalCode: "",
    countryCode: "",
    isVerified: false
  }
});

export const MultiPersonAdderFacility = ({
  ineligibleFacility,
  loading,
  peopleArray,
  setIneligibleFacility,
  gatheredFacilities,
  setSearchedForFacility,
  setVariables,
  setCurrentFacility,
  variables
}: FacilityType) => {
  const {
    query: facilityQuery,
    displayValue,
    results: facilityResults,
    isLoading: facilityLoading,
    failed: facilityFailed,
    highlightedIndex,
    setHighlightedIndex,
    handleQueryChange,
    handleClear: handleFacilityClear,
    handleItemSelected,
    setIsFocused,
    setDisplayValue,
    focusInput,
    itemRefs,
    resultsContainerRef,
    searchBoxRef
  } = useCreateInteractionFuzzySearch({
    entity: "facilities",
    getDisplayValue: getFacilityDisplayName,
    onSelect: (hit: FacilityHit) => {
      setIneligibleFacility(undefined);
      setCurrentFacility(mapHitToFacility(hit, gatheredFacilities.length));
      setSearchedForFacility(true);
    }
  });

  const hasActiveFacilityQuery = facilityQuery.trim() !== "";

  useEffect(() => {
    focusInput();
  }, [focusInput]);

  const handleClearFacility = () => {
    handleFacilityClear();
    setDisplayValue(undefined);
    setCurrentFacility(undefined);
    setSearchedForFacility(false);
    setIneligibleFacility(undefined);
  };

  const facilityTrailingIcon =
    displayValue || facilityQuery ? (
      <button
        type="button"
        className="cursor-pointer flex items-center h-full"
        onClick={handleClearFacility}
        aria-label="Clear search query"
      >
        <Icon name="cancel" />
      </button>
    ) : undefined;

  const selectedPeople = peopleArray.filter((person) => person.selected);
  const availablePeople = peopleArray.filter((person) => !person.selected);

  return (
    <div className="space-y-6">
      <div ref={searchBoxRef}>
        <TextField
          label="Facility"
          placeholder="Search for a facility"
          value={displayValue || facilityQuery}
          onChangeText={(value) => {
            setDisplayValue(undefined);
            handleQueryChange(value);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          leadingIcon="search"
          trailingIcon={facilityTrailingIcon}
          variant="outlined"
          className="w-full"
          supportingText="Search for a facility and then select people to add to the interaction"
          data-testid="facility-search-input"
        />
      </div>

      {hasActiveFacilityQuery && (
        <AlgoliaSearchResults
          results={facilityResults}
          query={facilityQuery}
          isLoading={facilityLoading}
          highlightedIndex={highlightedIndex}
          setHighlightedIndex={setHighlightedIndex}
          handleItemSelected={handleItemSelected}
          itemRefs={itemRefs}
          resultsContainerRef={resultsContainerRef}
          renderHit={renderCreateInteractionFacilityHit}
          noResultsMessage={facilityFailed ? "Couldn't load facilities" : "No facilities found"}
          className="mt-4"
        />
      )}

      {ineligibleFacility && <UnavailableFacilityBanner facility={ineligibleFacility} />}

      {!hasActiveFacilityQuery && (
        <PeopleSelectionLists
          selectedPeople={selectedPeople}
          availablePeople={availablePeople}
          loading={loading}
          onSelect={(person) => {
            person.selected = true;
            setVariables({ ...variables, participantIds: [...(variables.participantIds || []), person.id] });
          }}
          onDeselect={(person) => {
            person.selected = false;
            setVariables({
              ...variables,
              participantIds: (variables.participantIds || []).filter((id: string) => id !== person.id)
            });
          }}
          onSelectAll={() => {
            availablePeople.forEach((p) => {
              p.selected = true;
            });
            setVariables({
              ...variables,
              participantIds: [...(variables.participantIds || []), ...availablePeople.map((p) => p.id)]
            });
          }}
          onDeselectAll={() => {
            selectedPeople.forEach((p) => {
              p.selected = false;
            });
            const idsToRemove = new Set(selectedPeople.map((p) => p.id));
            setVariables({
              ...variables,
              participantIds: (variables.participantIds || []).filter((id: string) => !idsToRemove.has(id))
            });
          }}
        />
      )}
    </div>
  );
};
